import React, { useState, useEffect } from 'react';
import {
  Search, HelpCircle, GraduationCap, ExternalLink, Loader, User, Target,
  Calendar, Eye, ArrowRight, Check, X, Award, ShieldCheck, Clock,
  ShieldAlert, BookOpen, Sparkles, SlidersHorizontal, ArrowUpDown
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';

// Dynamic color matching for scholarships
const getScholarshipTheme = (name = '') => {
  const lower = name.toLowerCase();
  if (lower.includes('merit') || lower.includes('reliance')) {
    return {
      color: '#2563eb', // Vivid Blue
      colorRgb: '37,99,235',
      bg: 'rgba(37,99,235,0.06)',
      labelBg: 'rgba(37,99,235,0.1)',
      icon: GraduationCap,
      badge: 'Featured',
      badgeBg: 'rgba(37,99,235,0.15)',
      badgeColor: '#2563eb'
    };
  }
  if (lower.includes('leader') || lower.includes('google') || lower.includes('future')) {
    return {
      color: '#8b5cf6', // Purple
      colorRgb: '139,92,246',
      bg: 'rgba(139,92,246,0.06)',
      labelBg: 'rgba(139,92,246,0.1)',
      icon: Award,
      badge: 'Global Tech',
      badgeBg: 'rgba(139,92,246,0.15)',
      badgeColor: '#8b5cf6'
    };
  }
  if (lower.includes('inspire') || lower.includes('academic') || lower.includes('ignite')) {
    return {
      color: '#ea580c', // Bright Orange
      colorRgb: '234,88,12',
      bg: 'rgba(234,88,12,0.06)',
      labelBg: 'rgba(234,88,12,0.1)',
      icon: Sparkles,
      badge: 'Top Science',
      badgeBg: 'linear-gradient(135deg, #fef3c7, #fde68a)',
      badgeColor: '#d97706'
    };
  }
  if (lower.includes('pragati') || lower.includes('women') || lower.includes('girl')) {
    return {
      color: '#ec4899', // Pink
      colorRgb: '236,72,153',
      bg: 'rgba(236,72,153,0.06)',
      labelBg: 'rgba(236,72,153,0.1)',
      icon: Award,
      badge: 'Women in STEM',
      badgeBg: 'rgba(236,72,153,0.15)',
      badgeColor: '#ec4899'
    };
  }
  if (lower.includes('tata') || lower.includes('research') || lower.includes('pmrf')) {
    return {
      color: '#10b981', // Emerald Green
      colorRgb: '16,185,129',
      bg: 'rgba(16,185,129,0.06)',
      labelBg: 'rgba(16,185,129,0.1)',
      icon: ShieldCheck,
      badge: 'Premier Grant',
      badgeBg: 'rgba(16,185,129,0.15)',
      badgeColor: '#10b981'
    };
  }
  return {
    color: '#0284c7', // Sky Blue
    colorRgb: '2,132,199',
    bg: 'rgba(2,132,199,0.06)',
    labelBg: 'rgba(2,132,199,0.1)',
    icon: GraduationCap,
    badge: 'Merit Grant',
    badgeBg: 'rgba(2,132,199,0.15)',
    badgeColor: '#0284c7'
  };
};

/* ─── Verification Status ─── */
const getVerificationStatus = (scholarship) => {
  if (scholarship.verified === true) return 'verified';
  if (scholarship.verified === false) return 'not_verified';
  if (scholarship.verified === 'pending' || scholarship.status === 'in_progress') return 'in_progress';
  if (scholarship.status === 'verified') return 'verified';
  if (scholarship.provider && scholarship.amount && scholarship.deadline) return 'verified';
  return 'not_verified';
};

const verificationConfig = {
  verified: {
    label: 'Verified',
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.08)',
    border: 'rgba(16, 185, 129, 0.25)',
    icon: ShieldCheck,
  },
  in_progress: {
    label: 'In Progress',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.25)',
    icon: Clock,
  },
  not_verified: {
    label: 'Not Verified',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.08)',
    border: 'rgba(239, 68, 68, 0.25)',
    icon: ShieldAlert,
  },
};

function VerificationBadge({ status, size = 'sm' }) {
  const config = verificationConfig[status] || verificationConfig.not_verified;
  const Icon = config.icon;
  const isSm = size === 'sm';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: isSm ? '0.25rem' : '0.375rem',
      padding: isSm ? '0.2rem 0.5rem' : '0.3rem 0.75rem',
      borderRadius: '9999px',
      background: config.bg,
      border: `1px solid ${config.border}`,
      color: config.color,
      fontSize: isSm ? '0.625rem' : '0.75rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
    }}>
      <Icon size={isSm ? 10 : 13} />
      {config.label}
    </span>
  );
}

/* ─── Education Level Categories ─── */
const educationLevels = [
  { id: 'all', label: 'All Levels', short: 'All', icon: GraduationCap, color: '#9333ea' },
  { id: 'Class 8-10', label: 'Class 8–10', short: 'School (8-10)', icon: BookOpen, color: '#f59e0b' },
  { id: 'Class 11-12', label: 'Class 11–12', short: 'Higher Sec (11-12)', icon: Award, color: '#ec4899' },
  { id: 'Undergraduate (UG)', label: 'Undergraduate (UG)', short: 'UG Degrees', icon: GraduationCap, color: '#2563eb' },
  { id: 'B.Tech / Engineering', label: 'B.Tech / Engineering', short: 'B.Tech & Tech', icon: Sparkles, color: '#10b981' },
  { id: 'Postgraduate (PG)', label: 'Postgraduate (PG / Research)', short: 'PG & Research', icon: ShieldCheck, color: '#6366f1' },
];

/* ─── Scholarship Card Row ─── */
function ScholarshipCard({ s, onApply, onViewDetails, appliedApp, onUpdateStatus }) {
  const theme = getScholarshipTheme(s.name);
  const Icon = theme.icon;
  const vStatus = getVerificationStatus(s);

  // Calculate days left dynamically
  const daysLeft = s.deadline ? Math.ceil((new Date(s.deadline) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const daysLeftLabel = daysLeft > 0 ? `${daysLeft} Days Left` : daysLeft === 0 ? 'Today' : 'Expired';

  const appStatus = appliedApp?.status || 'pending';
  const isAwarded = appStatus === 'awarded' || appStatus === 'accepted';
  const isRejected = appStatus === 'rejected';

  return (
    <div 
      className="scholarship-card-container" 
      style={{
        background: '#ffffff',
        borderRadius: '1.25rem',
        border: `1.5px solid ${isAwarded ? '#10b98140' : 'rgba(0,0,0,0.06)'}`,
        borderLeft: `5px solid ${theme.color}`,
        padding: '1.4rem 1.6rem',
        boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
        transition: 'all 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.1rem',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top Row: Icon, Title, Info, and Action Buttons */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1.25rem', flexWrap: 'wrap' }}>
        
        {/* Left Section: Icon + Title Details */}
        <div style={{ display: 'flex', gap: '1.1rem', alignItems: 'flex-start', flex: '1 1 360px', minWidth: 260 }}>
          {/* Icon Bubble */}
          <div style={{
            width: '3.5rem', height: '3.5rem', borderRadius: '1rem',
            background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1.5px solid ${theme.color}25`, boxShadow: `0 4px 12px ${theme.color}15`,
            flexShrink: 0
          }}>
            <Icon size={22} color={theme.color} />
          </div>

          {/* Title & Provider */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
              <span style={{
                fontSize: '0.625rem', fontWeight: 800, padding: '0.15rem 0.55rem',
                borderRadius: '9999px', background: theme.badgeBg, color: theme.badgeColor,
                textTransform: 'uppercase', letterSpacing: '0.04em'
              }}>
                ★ {theme.badge}
              </span>
              <VerificationBadge status={vStatus} size="sm" />
              {appliedApp && (
                <span style={{
                  fontSize: '0.65rem', fontWeight: 800, padding: '0.15rem 0.55rem', borderRadius: '9999px',
                  background: isAwarded ? 'rgba(16,185,129,0.12)' : isRejected ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.12)',
                  color: isAwarded ? '#10b981' : isRejected ? '#ef4444' : '#d97706',
                  border: `1px solid ${isAwarded ? '#10b98140' : isRejected ? '#ef444440' : '#f59e0b40'}`,
                  textTransform: 'uppercase', letterSpacing: '0.04em'
                }}>
                  {isAwarded ? '🏆 Awarded' : isRejected ? '✕ Rejected' : '⏳ In Progress'}
                </span>
              )}
            </div>

            <h4 style={{ 
              color: 'var(--navy-dark)', 
              fontWeight: 800, 
              fontSize: '1.12rem', 
              lineHeight: 1.3, 
              margin: '0 0 0.25rem 0',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {s.name}
            </h4>

            {/* One-Liner Info: Provider + Description Snippet */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              fontSize: '0.8rem', 
              lineHeight: 1.4,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              margin: 0
            }}>
              <span style={{ color: theme.color, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                {s.provider || '—'} <Check size={10} color="#ffffff" style={{ background: '#3b82f6', borderRadius: '50%', padding: '1px', width: '12px', height: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} />
              </span>
              <span style={{ color: '#cbd5e1', flexShrink: 0 }}>•</span>
              <span style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.description}
              </span>
            </div>
          </div>
        </div>

        {/* Right Section: Action Buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexShrink: 0, justifyContent: 'flex-end', minWidth: 200 }}>
          <button
            onClick={() => onViewDetails(s)}
            style={{
              border: `1.5px solid ${theme.color}40`, color: theme.color, background: '#ffffff',
              padding: '0.55rem 1.1rem', borderRadius: '0.75rem', fontSize: '0.8rem', fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '0.4rem',
              justifyContent: 'center', transition: 'all 0.2s', whiteSpace: 'nowrap'
            }}
            onMouseOver={e => { e.currentTarget.style.background = theme.bg; }}
            onMouseOut={e => { e.currentTarget.style.background = '#ffffff'; }}
          >
            <Eye size={14} /> View Details
          </button>
          
          {appliedApp ? (
            isAwarded || isRejected ? (
              <span style={{
                padding: '0.55rem 0.95rem',
                borderRadius: '0.75rem',
                fontSize: '0.78rem',
                fontWeight: 800,
                fontFamily: 'inherit',
                border: `1.5px solid ${isAwarded ? '#10b981' : '#ef4444'}`,
                background: isAwarded ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.08)',
                color: isAwarded ? '#059669' : '#dc2626',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
              }}>
                {isAwarded ? '🏆 Awarded (Final)' : '✕ Rejected (Final)'}
              </span>
            ) : (
              <select
                value={appliedApp.status || 'in_progress'}
                onChange={(e) => onUpdateStatus(appliedApp.id || appliedApp._id, e.target.value, s)}
                style={{
                  padding: '0.55rem 0.85rem',
                  borderRadius: '0.75rem',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  border: '1.5px solid #f59e0b',
                  background: 'rgba(245,158,11,0.08)',
                  color: '#d97706',
                  outline: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                <option value="in_progress">⏳ In Progress</option>
                <option value="awarded">🏆 Awarded</option>
                <option value="rejected">✕ Rejected</option>
              </select>
            )
          ) : (
            <button
              onClick={() => onApply(s)}
              style={{
                background: `linear-gradient(135deg, ${theme.color} 0%, ${theme.color}dd 100%)`, color: '#ffffff',
                padding: '0.55rem 1.25rem', borderRadius: '0.75rem', fontSize: '0.8rem', fontWeight: 800,
                cursor: 'pointer', border: 'none', fontFamily: 'inherit', display: 'flex', alignItems: 'center',
                gap: '0.4rem', justifyContent: 'center', transition: 'all 0.2s', boxShadow: `0 4px 10px ${theme.color}25`,
                whiteSpace: 'nowrap'
              }}
              onMouseOver={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseOut={e => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none'; }}
            >
              Apply Now <ArrowRight size={14} />
            </button>
          )}
        </div>

      </div>

      {/* Bottom Stats Strip */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '0.75rem',
        padding: '0.75rem 1.15rem',
        background: '#f8fafc',
        borderRadius: '0.85rem',
        border: '1px solid #eef2f6'
      }}>
        {/* Education Level */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <User size={13} color={theme.color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em' }}>Education Level</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--navy-dark)', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{s.level || 'All Streams'}</span>
          </div>
        </div>

        {/* Selection Mode */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Target size={13} color={theme.color} />
          </div>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em' }}>Selection Mode</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--navy-dark)', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>Merit + Review</span>
          </div>
        </div>

        {/* Funding Award */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: 'rgba(16,185,129,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 900, color: '#10b981' }}>₹</span>
          </div>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em' }}>Funding Award</span>
            <span style={{ fontSize: '0.82rem', color: '#10b981', fontWeight: 800, display: 'block' }}>₹{s.amount ? s.amount.toLocaleString('en-IN') : '—'} <small style={{ fontSize: '0.625rem', color: '#64748b', fontWeight: 600 }}>Grant</small></span>
          </div>
        </div>

        {/* Deadline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
          <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Calendar size={13} color="#ef4444" />
          </div>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: '0.625rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em' }}>Deadline</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--navy-dark)', fontWeight: 700, display: 'block' }}>
              {s.deadline ? new Date(s.deadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              <span style={{ fontSize: '0.625rem', color: daysLeft > 0 ? '#ea580c' : '#ef4444', fontWeight: 800, marginLeft: '0.35rem' }}>({daysLeftLabel})</span>
            </span>
          </div>
        </div>

      </div>

    </div>
  );
}

/* ─── Detailed Scholarship Modal ─── */
function ScholarshipDetailModal({ scholarship, onClose, onApply, appliedApp, onUpdateStatus }) {
  const theme = getScholarshipTheme(scholarship.name);
  const vStatus = getVerificationStatus(scholarship);
  const vConfig = verificationConfig[vStatus] || verificationConfig.not_verified;
  const VIcon = vConfig.icon;
  const daysLeft = scholarship.deadline ? Math.ceil((new Date(scholarship.deadline) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  const appStatus = appliedApp?.status || 'pending';
  const isAwarded = appStatus === 'awarded' || appStatus === 'accepted';
  const isRejected = appStatus === 'rejected';

  // Close on Escape key
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
        overflowY: 'auto',
      }}
    >
      {/* Dark blur overlay */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(6px)',
          zIndex: 1,
        }}
        onClick={onClose}
      />
      
      {/* Detailed Card Container */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          background: '#ffffff',
          borderRadius: '1.75rem',
          maxWidth: '680px',
          width: '92vw',
          maxHeight: '88vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.35)',
          animation: 'scaleIn 0.22s cubic-bezier(0.25, 1, 0.5, 1) both',
        }}
      >
        {/* Top colorful banner */}
        <div
          style={{
            height: 120,
            position: 'relative',
            padding: '1.5rem 2rem',
            display: 'flex',
            alignItems: 'flex-end',
            background: `linear-gradient(135deg, ${theme.color}dd 0%, ${theme.color} 100%)`,
            flexShrink: 0,
          }}
        >
          <button 
            onClick={onClose}
            style={{
              position: 'absolute', top: '1rem', right: '1rem',
              background: 'rgba(255, 255, 255, 0.2)', border: 'none',
              color: '#ffffff', width: '2.25rem', height: '2.25rem',
              borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', zIndex: 2
            }}
          >
            <X size={16} />
          </button>

          <div style={{
            width: '3.75rem', height: '3.75rem', borderRadius: '1.25rem', background: '#ffffff',
            boxShadow: '0 8px 20px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'absolute', bottom: '-1.75rem', left: '2rem',
            border: '3px solid #ffffff'
          }}>
            <GraduationCap size={24} color={theme.color} />
          </div>
        </div>

        <div style={{ padding: '2.5rem 2rem 2rem', overflowY: 'auto', flex: 1 }}>
          {/* Title + Verification badge */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
              <h3 style={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--navy-dark)', margin: 0, lineHeight: 1.3 }}>
                {scholarship.name}
              </h3>
              <VerificationBadge status={vStatus} size="md" />
            </div>
            <p style={{ color: theme.color, fontSize: '0.875rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.25rem', margin: 0 }}>
              {scholarship.provider} <Check size={12} color="#ffffff" style={{ background: '#3b82f6', borderRadius: '50%', padding: '1px', width: '14px', height: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} />
            </p>
          </div>

          {/* Verification Status Banner */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            background: vConfig.bg, border: `1px solid ${vConfig.border}`,
            borderRadius: '0.875rem', padding: '0.75rem 1rem', marginBottom: '1.25rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', borderRadius: '50%',
              background: `${vConfig.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <VIcon size={15} color={vConfig.color} />
            </div>
            <div>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: vConfig.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {vConfig.label}
              </span>
              <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0, lineHeight: 1.4 }}>
                {vStatus === 'verified' && 'This scholarship has been verified by the Ekalavya team for authentic grant disbursement.'}
                {vStatus === 'in_progress' && 'Verification in progress with the institutional provider.'}
                {vStatus === 'not_verified' && 'New submission pending initial verification.'}
              </p>
            </div>
          </div>

          {/* Application Status if already applied */}
          {appliedApp && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: isAwarded ? 'rgba(16,185,129,0.06)' : isRejected ? 'rgba(239,68,68,0.05)' : 'rgba(245,158,11,0.06)',
              border: `1px solid ${isAwarded ? '#10b98135' : isRejected ? '#ef444435' : '#f59e0b35'}`,
              borderRadius: '0.875rem', padding: '0.85rem 1.15rem', marginBottom: '1.25rem',
            }}>
              <div>
                <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>Your Application Status</span>
                <span style={{ fontSize: '0.9rem', fontWeight: 800, color: isAwarded ? '#059669' : isRejected ? '#dc2626' : '#d97706' }}>
                  {isAwarded ? '🏆 Grant Awarded' : isRejected ? '✕ Application Not Selected' : '⏳ Application Under Review (In Progress)'}
                </span>
              </div>
              {isAwarded || isRejected ? (
                <span style={{
                  padding: '0.45rem 0.85rem',
                  borderRadius: '0.65rem',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  border: `1.5px solid ${isAwarded ? '#10b981' : '#ef4444'}`,
                  background: isAwarded ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
                  color: isAwarded ? '#059669' : '#dc2626',
                  whiteSpace: 'nowrap',
                }}>
                  {isAwarded ? '🏆 Final (Awarded)' : '✕ Final (Rejected)'}
                </span>
              ) : (
                <select
                  value={appliedApp.status || 'in_progress'}
                  onChange={(e) => onUpdateStatus(appliedApp.id || appliedApp._id, e.target.value, scholarship)}
                  style={{
                    padding: '0.45rem 0.75rem',
                    borderRadius: '0.65rem',
                    fontSize: '0.78rem',
                    fontWeight: 800,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    border: '1.5px solid #f59e0b',
                    background: '#ffffff',
                    color: '#d97706',
                    outline: 'none',
                  }}
                >
                  <option value="in_progress">⏳ In Progress</option>
                  <option value="awarded">🏆 Awarded</option>
                  <option value="rejected">✕ Rejected</option>
                </select>
              )}
            </div>
          )}

          {/* About */}
          <div style={{ marginBottom: '1.25rem' }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--navy-dark)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>About Opportunity</h4>
            <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: '1.6', margin: 0 }}>
              {scholarship.description || "Official scholarship opportunity with structured financial support for meritorious students."}
            </p>
          </div>

          {/* Details Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '0.2rem' }}>Target Education Level</span>
              <span style={{ fontSize: '0.85rem', color: 'var(--navy-dark)', fontWeight: '800' }}>{scholarship.level || 'All Levels'}</span>
            </div>
            <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '0.2rem' }}>Selection Mode</span>
              <span style={{ fontSize: '0.85rem', color: 'var(--navy-dark)', fontWeight: '700' }}>Merit & Verification Review</span>
            </div>
            <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '0.2rem' }}>Funding Amount</span>
              <span style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: '800' }}>{scholarship.amount ? `₹${scholarship.amount.toLocaleString('en-IN')}` : 'Variable'}</span>
            </div>
            <div style={{ background: '#f8fafc', padding: '0.75rem 1rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '0.2rem' }}>Application Deadline</span>
              <span style={{ fontSize: '0.85rem', color: daysLeft > 0 ? '#ea580c' : '#ef4444', fontWeight: '800' }}>
                {scholarship.deadline ? new Date(scholarship.deadline).toLocaleDateString('en-IN') : 'Ongoing'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {!appliedApp && (
              <button
                onClick={() => { onApply(scholarship); onClose(); }}
                style={{ flex: 1, padding: '0.875rem', background: `linear-gradient(135deg, ${theme.color} 0%, ${theme.color}dd 100%)`, color: '#ffffff', borderRadius: '0.875rem', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', border: 'none', transition: 'all 0.2s', fontFamily: 'inherit', boxShadow: `0 4px 14px ${theme.color}35` }}
              >
                Apply Now
              </button>
            )}
            
            {scholarship.link && (
              <a 
                href={scholarship.link} 
                target="_blank" 
                rel="noreferrer" 
                style={{ flex: appliedApp ? 1 : undefined, padding: '0.875rem 1.25rem', background: '#f8fafc', color: '#475569', borderRadius: '0.875rem', fontWeight: 700, fontSize: '0.85rem', border: '1px solid #cbd5e1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                Official Portal <ExternalLink size={14} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Scholarship Hub ─── */
export default function Scholarships() {
  const [scholarships, setScholarships] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [applications, setApplications] = useState([]);
  const [activeTab, setActiveTab] = useState('browse'); // 'browse' | 'applications'
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAmount, setFilterAmount] = useState('all');
  const [sortBy, setSortBy] = useState('deadline'); // 'deadline' | 'amount_desc' | 'amount_asc' | 'name' | 'verified'
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const { user } = useAuth();
  const { showAlert } = useAlert();

  const fetchScholarships = () => {
    api.getCollection('scholarships').then(data => {
      setScholarships(data || []);
      setFiltered(data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const fetchApplications = () => {
    if (!user) return;
    api.getMyScholarshipApplications().then(data => {
      setApplications(data || []);
    }).catch(() => setApplications([]));
  };

  useEffect(() => {
    fetchScholarships();
  }, []);

  useEffect(() => {
    if (user) {
      fetchApplications();
    }
  }, [user]);

  useEffect(() => {
    let result = [...scholarships];
    
    // 1. Education Level Filter
    if (selectedLevel !== 'all') {
      result = result.filter(s => {
        if (!s.level) return false;
        if (selectedLevel === 'Class 8-10') {
          return s.level.includes('8-10') || s.level.includes('6-10') || s.level.includes('School');
        }
        if (selectedLevel === 'Class 11-12') {
          return s.level.includes('11-12') || s.level.includes('Secondary');
        }
        if (selectedLevel === 'Undergraduate (UG)') {
          return s.level.includes('Undergraduate') || s.level.includes('(UG)');
        }
        if (selectedLevel === 'B.Tech / Engineering') {
          return s.level.includes('B.Tech') || s.level.includes('Engineering');
        }
        if (selectedLevel === 'Postgraduate (PG)') {
          return s.level.includes('Postgraduate') || s.level.includes('(PG)') || s.level.includes('Ph.D') || s.level.includes('Research');
        }
        return s.level === selectedLevel;
      });
    }

    // 2. Text Search Filter
    const q = search.toLowerCase();
    if (q) {
      result = result.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q) ||
        s.provider?.toLowerCase().includes(q) ||
        s.level?.toLowerCase().includes(q)
      );
    }
    
    // 3. Verification Status Filter
    if (filterStatus !== 'all') {
      result = result.filter(s => getVerificationStatus(s) === filterStatus);
    }
    
    // 4. Amount Filter
    if (filterAmount !== 'all') {
      const minAmount = parseInt(filterAmount, 10);
      result = result.filter(s => s.amount && s.amount >= minAmount);
    }

    // 5. Sorting
    result.sort((a, b) => {
      if (sortBy === 'deadline') {
        const dA = a.deadline ? new Date(a.deadline).getTime() : 9999999999999;
        const dB = b.deadline ? new Date(b.deadline).getTime() : 9999999999999;
        return dA - dB;
      }
      if (sortBy === 'amount_desc') {
        return (b.amount || 0) - (a.amount || 0);
      }
      if (sortBy === 'amount_asc') {
        return (a.amount || 0) - (b.amount || 0);
      }
      if (sortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '');
      }
      if (sortBy === 'verified') {
        const vA = getVerificationStatus(a) === 'verified' ? 1 : 0;
        const vB = getVerificationStatus(b) === 'verified' ? 1 : 0;
        return vB - vA;
      }
      return 0;
    });
    
    setFiltered(result);
  }, [search, selectedLevel, filterStatus, filterAmount, sortBy, scholarships]);

  const handleApply = async (s) => {
    if (!user) { showAlert('Login Required', 'Please login to apply for scholarships.'); return; }
    try {
      await api.createDocument('scholarship_applications', { scholarship_id: s.id, user_id: user.id, status: 'in_progress' });
      showAlert('Application Submitted', `Your application for ${s.name} has been recorded! You can now track or update its status under "My Applications".`);
      fetchApplications();
    } catch {
      showAlert('Already Applied', 'You have already applied for this scholarship. You can update its status in "My Applications".');
    }
  };

  const handleUpdateStatus = async (appId, newStatus, scholarshipObj) => {
    if (!user) return;
    try {
      await api.updateScholarshipApplicationStatus(appId, newStatus);
      fetchApplications();
      if (newStatus === 'awarded') {
        const amountStr = scholarshipObj?.amount ? `₹${scholarshipObj.amount.toLocaleString('en-IN')}` : 'grant';
        showAlert('🎉 Congratulations!', `Status updated to Awarded! ${amountStr} is now dynamically reflected in your Dashboard statistics.`);
      } else if (newStatus === 'rejected') {
        showAlert('Status Updated', 'Application marked as Rejected. Your total applied count remains safely preserved on the dashboard.');
      } else {
        showAlert('Status Updated', 'Application marked as In Progress. Total applied count is preserved.');
      }
    } catch (err) {
      showAlert('Update Failed', err.message || 'Could not update status. Please try again.');
    }
  };

  // Map of applied scholarships by scholarship_id
  const appliedMap = new Map();
  applications.forEach(app => {
    const sId = app.scholarship_id?._id || app.scholarship_id?.id || app.scholarship_id;
    if (sId) appliedMap.set(String(sId), app);
  });

  const awardedApps = applications.filter(a => a.status === 'awarded' || a.status === 'accepted');
  const inProgressApps = applications.filter(a => a.status === 'in_progress' || a.status === 'pending');
  const rejectedApps = applications.filter(a => a.status === 'rejected');
  const totalAwardedAmount = awardedApps.reduce((sum, a) => sum + (a.scholarship_id?.amount || 0), 0);

  // Helper count for tier badges
  const getLevelCount = (levelId) => {
    if (levelId === 'all') return scholarships.length;
    return scholarships.filter(s => {
      if (!s.level) return false;
      if (levelId === 'Class 8-10') return s.level.includes('8-10') || s.level.includes('6-10') || s.level.includes('School');
      if (levelId === 'Class 11-12') return s.level.includes('11-12') || s.level.includes('Secondary');
      if (levelId === 'Undergraduate (UG)') return s.level.includes('Undergraduate') || s.level.includes('(UG)');
      if (levelId === 'B.Tech / Engineering') return s.level.includes('B.Tech') || s.level.includes('Engineering');
      if (levelId === 'Postgraduate (PG)') return s.level.includes('Postgraduate') || s.level.includes('(PG)') || s.level.includes('Research');
      return s.level === levelId;
    }).length;
  };

  return (
    <div>
      {/* Top Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'rgba(147,51,234,0.08)', border: '1px solid rgba(147,51,234,0.18)', borderRadius: '9999px', padding: '0.3rem 0.85rem', color: 'var(--primary-gold)', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
            <Sparkles size={14} /> National & Corporate Grants
          </div>
          <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', fontWeight: 800, color: 'var(--navy-dark)', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
            Scholarship Hub
          </h2>
          <p className="secondary-header-text">
            Hand-picked opportunities categorized by education level, funding, and verified eligibility.
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <button
            onClick={() => showAlert('Scholarship Assistance', "Need help with your application? Our mentors are ready to guide you on essay writing, eligibility documents, and portal filing. We'll contact you on your registered email shortly.")}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 1.25rem', background: 'rgba(147, 51, 234, 0.06)', border: '1.5px solid rgba(147, 51, 234, 0.25)', color: 'var(--primary-gold)', fontSize: '0.85rem', fontWeight: 700, borderRadius: '0.75rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(147, 51, 234, 0.12)'}
            onMouseOut={e => e.currentTarget.style.background = 'rgba(147, 51, 234, 0.06)'}
          >
            <HelpCircle size={16} /> Need Assistance?
          </button>
        </div>
      </div>

      {/* Primary Tab Switcher */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.75rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
        <button
          onClick={() => setActiveTab('browse')}
          style={{
            padding: '0.65rem 1.25rem',
            borderRadius: '0.75rem',
            fontSize: '0.85rem',
            fontWeight: 800,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            border: activeTab === 'browse' ? '1.5px solid var(--primary-gold)' : '1px solid #e2e8f0',
            background: activeTab === 'browse' ? 'rgba(147,51,234,0.08)' : '#ffffff',
            color: activeTab === 'browse' ? 'var(--primary-gold)' : '#64748b',
            transition: 'all 0.2s',
          }}
        >
          <BookOpen size={16} /> Browse Opportunities ({scholarships.length})
        </button>

        <button
          onClick={() => setActiveTab('applications')}
          style={{
            padding: '0.65rem 1.25rem',
            borderRadius: '0.75rem',
            fontSize: '0.85rem',
            fontWeight: 800,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            border: activeTab === 'applications' ? '1.5px solid #10b981' : '1px solid #e2e8f0',
            background: activeTab === 'applications' ? 'rgba(16,185,129,0.08)' : '#ffffff',
            color: activeTab === 'applications' ? '#10b981' : '#64748b',
            transition: 'all 0.2s',
          }}
        >
          <Award size={16} /> My Applications & Status ({applications.length})
        </button>
      </div>

      {activeTab === 'applications' ? (
        /* ─── MY APPLICATIONS TAB ─── */
        <div>
          {/* Summary Stat Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '1.25rem', padding: '1.25rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#2563eb' }}/>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Applied</span>
              <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#2563eb', marginTop: '0.25rem' }}>{applications.length}</div>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Incremented on Dashboard</span>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '1.25rem', padding: '1.25rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#f59e0b' }}/>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>In Progress</span>
              <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#d97706', marginTop: '0.25rem' }}>{inProgressApps.length}</div>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Pending Decision</span>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '1.25rem', padding: '1.25rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#10b981' }}/>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Awarded Grants</span>
              <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#10b981', marginTop: '0.25rem' }}>{awardedApps.length}</div>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#10b981' }}>₹{totalAwardedAmount.toLocaleString('en-IN')} on Dashboard</span>
            </div>

            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '1.25rem', padding: '1.25rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#ef4444' }}/>
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Not Awarded</span>
              <div style={{ fontSize: '1.75rem', fontWeight: 900, color: '#ef4444', marginTop: '0.25rem' }}>{rejectedApps.length}</div>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Count kept in Total Applied</span>
            </div>
          </div>

          {/* List of Applications */}
          {applications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', background: '#ffffff', borderRadius: '1.25rem', border: '1.5px dashed #cbd5e1' }}>
              <GraduationCap size={44} color="#94a3b8" style={{ margin: '0 auto 0.75rem' }} />
              <h4 style={{ color: 'var(--navy-dark)', fontWeight: 800, marginBottom: '0.35rem' }}>No scholarship applications yet</h4>
              <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1.25rem' }}>Explore open opportunities and click "Apply Now" to start tracking.</p>
              <button
                onClick={() => setActiveTab('browse')}
                style={{ padding: '0.65rem 1.25rem', background: 'var(--primary-gold)', color: '#ffffff', border: 'none', borderRadius: '0.75rem', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Browse Scholarships →
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {applications.map(app => {
                const s = app.scholarship_id || {};
                const theme = getScholarshipTheme(s.name || '');
                const isAwarded = app.status === 'awarded' || app.status === 'accepted';
                const isRejected = app.status === 'rejected';

                return (
                  <div
                    key={app.id || app._id}
                    style={{
                      background: '#ffffff',
                      border: `1.5px solid ${isAwarded ? '#10b98140' : isRejected ? '#ef444430' : '#e2e8f0'}`,
                      borderRadius: '1.25rem',
                      padding: '1.25rem 1.5rem',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                      borderLeft: `5px solid ${isAwarded ? '#10b981' : isRejected ? '#ef4444' : '#f59e0b'}`,
                    }}
                  >
                    <div style={{ flex: '1 1 300px', minWidth: 260 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
                        <h4 style={{ color: 'var(--navy-dark)', fontWeight: 800, fontSize: '1.05rem', margin: 0 }}>
                          {s.name || 'Scholarship Opportunity'}
                        </h4>
                        <span style={{
                          fontSize: '0.65rem', fontWeight: 800, padding: '0.15rem 0.5rem', borderRadius: '9999px',
                          background: isAwarded ? 'rgba(16,185,129,0.1)' : isRejected ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                          color: isAwarded ? '#10b981' : isRejected ? '#ef4444' : '#d97706',
                          textTransform: 'uppercase',
                        }}>
                          {isAwarded ? '🏆 Awarded' : isRejected ? '✕ Rejected' : '⏳ In Progress'}
                        </span>
                      </div>
                      <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 0.5rem 0', fontWeight: 600 }}>
                        {s.provider || 'Provider'} • Target: {s.level || 'All Levels'}
                      </p>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#10b981' }}>
                          ₹{s.amount ? s.amount.toLocaleString('en-IN') : '—'} Award
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                          Applied: {app.created_at ? new Date(app.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Recently'}
                        </span>
                      </div>
                    </div>

                    {/* Interactive Status Switcher or Locked Decision Badge */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {isAwarded || isRejected ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Decision:</span>
                          <span style={{
                            padding: '0.45rem 0.85rem',
                            borderRadius: '0.5rem',
                            border: `1.5px solid ${isAwarded ? '#10b981' : '#ef4444'}`,
                            background: isAwarded ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.1)',
                            color: isAwarded ? '#059669' : '#dc2626',
                            fontSize: '0.78rem',
                            fontWeight: 800,
                          }}>
                            {isAwarded ? '🏆 Final Status: Awarded' : '✕ Final Status: Rejected'}
                          </span>
                        </div>
                      ) : (
                        <>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>Update Status:</span>
                          <div style={{ display: 'flex', background: '#f8fafc', padding: '0.25rem', borderRadius: '0.75rem', border: '1px solid #e2e8f0', gap: '0.25rem' }}>
                            {[
                              { id: 'in_progress', label: '⏳ In Progress', color: '#d97706', activeBg: 'rgba(245,158,11,0.15)' },
                              { id: 'awarded', label: '🏆 Awarded', color: '#059669', activeBg: 'rgba(16,185,129,0.18)' },
                              { id: 'rejected', label: '✕ Rejected', color: '#dc2626', activeBg: 'rgba(239,68,68,0.15)' },
                            ].map(st => {
                              const isCurrent = st.id === 'in_progress';
                              return (
                                <button
                                  key={st.id}
                                  onClick={() => handleUpdateStatus(app.id || app._id, st.id, s)}
                                  style={{
                                    padding: '0.4rem 0.75rem',
                                    borderRadius: '0.5rem',
                                    border: isCurrent ? `1.5px solid ${st.color}50` : 'none',
                                    background: isCurrent ? st.activeBg : 'transparent',
                                    color: isCurrent ? st.color : '#64748b',
                                    fontSize: '0.75rem',
                                    fontWeight: isCurrent ? 800 : 600,
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  {st.label}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ─── BROWSE OPPORTUNITIES TAB ─── */
        <div>
          {/* Education Level Category Cards / Selector */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <SlidersHorizontal size={14} color="var(--primary-gold)" /> Filter by Education Level
              </span>
              {selectedLevel !== 'all' && (
                <button
                  onClick={() => setSelectedLevel('all')}
                  style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary-gold)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  Reset to All ({scholarships.length})
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.85rem' }}>
              {educationLevels.map(lvl => {
                const LIcon = lvl.icon;
                const isSelected = selectedLevel === lvl.id;
                const count = getLevelCount(lvl.id);
                return (
                  <div
                    key={lvl.id}
                    onClick={() => setSelectedLevel(lvl.id)}
                    style={{
                      cursor: 'pointer',
                      padding: '0.85rem 1rem',
                      borderRadius: '1rem',
                      background: isSelected ? 'rgba(147,51,234,0.06)' : '#ffffff',
                      border: isSelected ? `2px solid ${lvl.color}` : '1.5px solid #e2e8f0',
                      boxShadow: isSelected ? `0 8px 20px ${lvl.color}20` : '0 2px 6px rgba(0,0,0,0.02)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      transition: 'all 0.2s ease',
                      transform: isSelected ? 'translateY(-2px)' : 'none',
                    }}
                  >
                    <div style={{
                      width: '2.5rem', height: '2.5rem', borderRadius: '0.75rem',
                      background: isSelected ? lvl.color : `${lvl.color}15`,
                      color: isSelected ? '#ffffff' : lvl.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, transition: 'all 0.2s'
                    }}>
                      <LIcon size={16} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <h5 style={{ margin: 0, fontSize: '0.85rem', fontWeight: 800, color: isSelected ? lvl.color : 'var(--navy-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {lvl.short}
                      </h5>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isSelected ? '#64748b' : '#94a3b8' }}>
                        {count} {count === 1 ? 'grant' : 'grants'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Filter & Sort Controls Bar */}
          <div style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '1.25rem', padding: '1rem 1.25rem', marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
            
            {/* Search Input */}
            <div style={{ position: 'relative', flex: '1 1 240px', minWidth: '220px' }}>
              <Search size={16} color="rgba(0,0,0,0.35)" style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                type="text"
                placeholder="Search scholarships, grants, providers…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', color: 'var(--navy-dark)', borderRadius: '0.75rem', padding: '0.625rem 1rem 0.625rem 2.4rem', fontSize: '0.875rem', outline: 'none', fontFamily: 'inherit' }}
              />
            </div>

            {/* Dropdown Filters & Sorting */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
              {/* Sort By Dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '0.15rem 0.65rem' }}>
                <ArrowUpDown size={14} color="#64748b" />
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--navy-dark)', padding: '0.45rem 0.25rem', fontSize: '0.82rem', fontWeight: 700, outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
                >
                  <option value="deadline">⏳ Deadline (Soonest)</option>
                  <option value="amount_desc">💰 Amount: High to Low</option>
                  <option value="amount_asc">💵 Amount: Low to High</option>
                  <option value="name">🔤 Name (A - Z)</option>
                  <option value="verified">🛡️ Verified First</option>
                </select>
              </div>

              {/* Verification Status */}
              <select 
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: 'var(--navy-dark)', borderRadius: '0.75rem', padding: '0.6rem 0.85rem', fontSize: '0.82rem', fontWeight: 600, outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
              >
                <option value="all">All Verification</option>
                <option value="verified">✓ Verified Only</option>
                <option value="in_progress">⏳ In Progress</option>
              </select>
              
              {/* Amount Filter */}
              <select 
                value={filterAmount}
                onChange={e => setFilterAmount(e.target.value)}
                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: 'var(--navy-dark)', borderRadius: '0.75rem', padding: '0.6rem 0.85rem', fontSize: '0.82rem', fontWeight: 600, outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
              >
                <option value="all">Any Amount</option>
                <option value="25000">₹25,000+</option>
                <option value="50000">₹50,000+</option>
                <option value="100000">₹1,00,000+</option>
              </select>
            </div>
          </div>

          {/* Listings */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '5rem', color: '#64748b' }}>
              <Loader size={48} style={{ margin: '0 auto 1rem', animation: 'spin 1s linear infinite' }} color="var(--primary-gold)" />
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              <p>Syncing with Scholarship Registry...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '5rem 2rem', background: '#ffffff', borderRadius: '1.25rem', border: '1.5px dashed #cbd5e1' }}>
              <GraduationCap size={44} color="#94a3b8" style={{ margin: '0 auto 0.75rem' }} />
              <h4 style={{ color: 'var(--navy-dark)', fontWeight: 800, marginBottom: '0.35rem' }}>No scholarships match your filters</h4>
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>Try clearing the search or switching the education level category.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {filtered.map(s => {
                const appliedApp = appliedMap.get(String(s.id));
                return (
                  <ScholarshipCard 
                    key={s.id} 
                    s={s} 
                    appliedApp={appliedApp}
                    onApply={handleApply} 
                    onViewDetails={setSelectedScholarship} 
                    onUpdateStatus={handleUpdateStatus}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* View Details Modal */}
      {selectedScholarship && (
        <ScholarshipDetailModal
          scholarship={selectedScholarship}
          appliedApp={appliedMap.get(String(selectedScholarship.id))}
          onClose={() => setSelectedScholarship(null)}
          onApply={handleApply}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </div>
  );
}
